<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { hasPressedMod } from 'shared/helpers/KeyboardHelpers';
import InternalChatMentionMenu from './InternalChatMentionMenu.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  contentFormat: { type: String, default: 'markdown' },
  disabled: { type: Boolean, default: false },
  canSubmit: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  editorKey: { type: String, default: '' },
  testId: { type: String, default: 'internal-chat-composer' },
  focusOnMount: { type: Boolean, default: false },
  members: { type: Object, default: () => ({ users: [], teams: [] }) },
  canMentionAll: { type: Boolean, default: false },
});
const emit = defineEmits([
  'update:modelValue',
  'update:contentFormat',
  'emptyChange',
  'submit',
  'typing',
]);
const editor = ref(null);
const root = ref(null);
const menu = ref(null);
const suggestion = ref({ active: false, query: '' });
const closeMentions = () => editor.value?.dismissMentionSuggestions();
const insertMention = ({ id, label, type }) =>
  editor.value?.insertMention({ id, name: label, type });
const toggleMentionMenu = () => {
  if (props.disabled) return;
  if (suggestion.value.active) closeMentions();
  else editor.value?.openMentionSuggestions();
};
const onBlur = () =>
  nextTick(() => {
    if (!root.value?.contains(document.activeElement)) closeMentions();
  });
watch(
  () => props.disabled,
  disabled => {
    if (disabled) closeMentions();
  }
);
const contentEmpty = ref(true);
const { isEditorHotKeyEnabled } = useUISettings();
const onInput = content => {
  emit('update:contentFormat', 'markdown');
  emit('update:modelValue', content);
};
const onKeydown = event => {
  if (suggestion.value.active && menu.value?.onKeydown(event)) return true;
  if (
    event.key !== 'Enter' ||
    event.shiftKey ||
    event.altKey ||
    event.isComposing ||
    event.keyCode === 229
  )
    return false;
  const enabled = hasPressedMod(event)
    ? isEditorHotKeyEnabled('cmd_enter')
    : !event.ctrlKey && !event.metaKey && isEditorHotKeyEnabled('enter');
  if (!enabled) return false;
  event.preventDefault();
  if (!props.disabled && props.canSubmit) emit('submit');
  return true;
};
defineExpose({
  focus: () => editor.value?.focus(),
  insertPlainText: text => editor.value?.insertPlainText(text),
  insertMention,
  toggleMentionMenu,
  mentionMenuOpen: computed(() => suggestion.value.active),
});
</script>

<template>
  <div
    ref="root"
    class="relative [&_.ProseMirror-separator]:!inline [&_.prosemirror-mention-node]:rounded [&_.prosemirror-mention-node]:font-medium [&_.prosemirror-mention-node]:bg-n-amber-3 [&_.prosemirror-mention-node]:text-n-slate-12 [&_.prosemirror-mention-node]:py-0 [&_.prosemirror-mention-node]:px-1 [&_.prosemirror-mention-node]:ring-1 [&_.prosemirror-mention-node]:ring-inset [&_.prosemirror-mention-node]:ring-n-amber-6"
  >
    <Editor
      ref="editor"
      :model-value="modelValue"
      :plain-text="contentFormat !== 'markdown'"
      escape-plain-text
      :editor-key="editorKey"
      :editor-label="label"
      :editor-test-id="testId"
      :placeholder="placeholder"
      :focus-on-mount="focusOnMount"
      :disabled="disabled"
      :max-length="0"
      :show-character-count="false"
      :enable-suggestions="false"
      enable-structured-mentions
      :enable-canned-responses="false"
      :enable-variables="false"
      :enable-captain-tools="false"
      :enable-copilot-menu="false"
      :enable-inline-images="false"
      :enable-global-events="false"
      :allow-signature="false"
      :keydown-handler="onKeydown"
      @content-empty="
        contentEmpty = $event;
        emit('emptyChange', $event);
      "
      @update:model-value="onInput"
      @typing-on="!disabled && !contentEmpty && emit('typing', true)"
      @typing-off="emit('typing', false)"
      @mention-suggestion-change="suggestion = $event"
      @blur="onBlur"
    />
    <InternalChatMentionMenu
      v-if="suggestion.active && !disabled"
      ref="menu"
      :members="members"
      :can-mention-all="canMentionAll"
      :query="suggestion.query"
      @mention="insertMention"
      @close="closeMentions"
    />
  </div>
</template>
